// /app/frontend/src/components/NextVisitNudge.jsx
// PATCH B: Next-Visit Nudge - Shows tomorrow's theme when exiting a room

import React, { useEffect, useState, useCallback } from 'react';
import { X, Sparkles, Calendar } from 'lucide-react';

// Tomorrow's day themes (matches backend community_rhythm.py)
const WEEKDAY_THEMES = {
  0: { day: 'Sunday', theme: 'Reflection', tagline: 'Honoring where you are' },
  1: { day: 'Monday', theme: 'Reset', tagline: 'Starting fresh, together' },
  2: { day: 'Tuesday', theme: 'Growth', tagline: 'Learning from each other' },
  3: { day: 'Wednesday', theme: 'Connection', tagline: 'Deepening our bonds' },
  4: { day: 'Thursday', theme: 'Gratitude', tagline: 'Appreciating the journey' },
  5: { day: 'Friday', theme: 'Gratitude', tagline: 'Celebrating small wins' },
  6: { day: 'Saturday', theme: 'Play', tagline: 'Light hearts, open minds' },
};

/**
 * NextVisitNudge - Shows when user leaves a room
 * 
 * Features:
 * - Shows tomorrow's community theme
 * - Auto-dismisses after 5 seconds
 * - Can be manually dismissed
 * - Subtle and non-intrusive
 * 
 * @param {boolean} show - Whether to show the nudge
 * @param {function} onDismiss - Callback when dismissed
 */
export default function NextVisitNudge({ show, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Get tomorrow's theme
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowWeekday = tomorrow.getDay();
  const tomorrowTheme = WEEKDAY_THEMES[tomorrowWeekday];

  const handleDismiss = useCallback(() => {
    setVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  useEffect(() => {
    if (show) {
      setVisible(true);
      setCountdown(5);
    }
  }, [show]);

  // Auto-dismiss after countdown
  useEffect(() => {
    if (!visible) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleDismiss();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [visible, handleDismiss]);

  if (!visible || !tomorrowTheme) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-slide-up"
      data-testid="next-visit-nudge"
    >
      <div className="rounded-2xl bg-gradient-to-r from-rose-500/95 to-purple-500/95 backdrop-blur-sm p-4 shadow-lg text-white">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="text-xs font-medium opacity-90">Tomorrow</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/70 hover:text-white transition p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Theme Info */}
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-amber-300" />
          <span className="font-semibold">{tomorrowTheme.theme}</span>
        </div>
        <p className="text-sm text-white/80 mb-2">
          {tomorrowTheme.tagline}
        </p>

        {/* Subtle CTA */}
        <div className="text-xs text-white/60 flex items-center justify-between">
          <span>See you {tomorrowTheme.day.toLowerCase()}!</span>
          <span className="opacity-50">{countdown}s</span>
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-0.5 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white/50 transition-all duration-1000 ease-linear"
            style={{ width: `${(countdown / 5) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
